import React from 'react';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

import {useExperimentsStore} from 'apps/experiments/src/store';
import {
  ExperimentEndConditionType,
  ExperimentStatus,
  FlattenedExperimentMetricResults,
  StatisticallySignificant,
} from 'iron/apps/experiments/src/types';
import {InsightsQueryResults} from 'common/report/insights/util/types';

import {getLatestOverallSeriesValue, getLatestSeriesValues} from '../query-utils';
import {useExperimentPageContext} from '../context';
import {useForceConcludeExperiment} from './force-conclude-experiment';
import {useUpdateExperiment} from './update-experiment';
import {useFlattenedMetricResults} from './metric-results';
import {useStagedExperiment} from './staged-experiment';

const CACHE_RESULTS_STATUSES = new Set([ExperimentStatus.CONCLUDED, ExperimentStatus.SUCCESS, ExperimentStatus.FAIL]);
/**
 * React hook that synchronizes experiment exposure and metric results caches,
 * and handles automatic experiment conclusion logic.
 *
 * This hook:
 * - Updates the experiment's exposure cache in the store if exposures have changed.
 * - Updates the experiment's metric results cache in the store if metric results have changed.
 * - Automatically concludes the experiment based on its configured end condition:
 *   - Concludes when the sample size is reached (if end condition is SAMPLE_SIZE).
 *   - Concludes when the configured number of days has passed since the start date (if end condition is DAYS).
 *   - Concludes when the experiment's end date has passed (default case).
 *
 * The hook only runs if the user has edit permissions and the experiment is currently active.
 *
 * Dependencies:
 * - Uses context and store hooks to access experiment data and update functions.
 * - Uses moment.js for date calculations.
 * - Uses lodash for deep equality checks.
 *
 * @returns void
 */
export function useSyncResults() {
  const {canEdit} = useExperimentPageContext();
  // only use the saved experiment because we don't want to make state updates based off of draft changes
  const experiment = useExperimentsStore((state) => state.experiment)!;
  const {hasStagedChanges} = useStagedExperiment();
  const exposuresQueryResults = useExperimentsStore((state) => state.exposuresQueryResults);
  const isLoadingMetricResults = useExperimentsStore((state) => state.isLoadingMetricResults);
  const flattenedMetricResults = useFlattenedMetricResults();

  const forceConclude = useForceConcludeExperiment();
  const updateExperiment = useUpdateExperiment();

  const overallExposures = getLatestOverallSeriesValue(exposuresQueryResults as InsightsQueryResults<number>);
  const exposures = getLatestSeriesValues(exposuresQueryResults as InsightsQueryResults<number>);

  // auto conclude experiment based on end condition
  React.useEffect(() => {
    if (!canEdit || !experiment || experiment.status !== ExperimentStatus.ACTIVE) {
      return;
    }

    switch (experiment.settings.endCondition) {
      case ExperimentEndConditionType.SAMPLE_SIZE:
        if (experiment.settings.sampleSize && overallExposures >= experiment.settings.sampleSize) {
          forceConclude();
        }
        return;
      case ExperimentEndConditionType.DAYS:
        if (
          experiment?.start_date &&
          moment(experiment.start_date).add(experiment.settings.endAfterDays, `days`).isSameOrBefore(moment())
        ) {
          forceConclude();
        }
        return;
      default:
        if (experiment?.end_date && moment(experiment.end_date).isSameOrBefore(moment())) {
          forceConclude();
        }
    }
  }, [canEdit, experiment?.end_date, experiment?.settings.sampleSize, overallExposures, forceConclude]);

  if (!experiment || !canEdit) {
    return;
  }

  // sync exposures to cache
  if (!isEmpty(exposures) && !isEqual(experiment.exposures_cache, exposures)) {
    updateExperiment({
      id: experiment.id,
      exposures_cache: exposures,
    });
  }

  // sync results to cache
  (() => {
    if (isLoadingMetricResults || hasStagedChanges || !CACHE_RESULTS_STATUSES.has(experiment.status)) {
      return;
    }
    const resultsSummary = flattenedMetricResults.reduce(
      (acc, metric) => {
        acc[metric.statisticallySignificant] = [
          ...(acc[metric.statisticallySignificant] || []),
          {
            ...metric,
          },
        ];
        return acc;
      },
      {} as Record<StatisticallySignificant, FlattenedExperimentMetricResults[]>,
    );
    if (!isEmpty(resultsSummary) && !isEqual(experiment.results_cache?.summary, resultsSummary)) {
      updateExperiment({
        id: experiment.id,
        results_cache: {
          summary: resultsSummary,
        },
      });
    }
  })();
}
